import { useMemo } from 'react';
import { getApiKey, MAPY_API_CONFIG, MapsetType } from '../config/mapyApi';

interface UseLeafletMapOptions {
    /** Typ mapové vrstvy (basic, outdoor, winter, aerial) */
    mapset?: MapsetType;
}

interface UseLeafletMapReturn {
    /** URL šablona pro tile vrstvu */
    tileUrl: string;
    /** Atribuce pro mapu */
    attribution: string;
    /** Dostupné mapové vrstvy */
    mapsets: typeof MAPY_API_CONFIG.tiles.mapsets;
    /** Generuje URL pro konkrétní mapset */
    getTileUrl: (mapset: MapsetType) => string;
}

/**
 * Hook pro práci s Leaflet mapou a Mapy.com tile API
 *
 * @example
 * const { tileUrl, attribution } = useLeafletMap({ mapset: 'outdoor' });
 *
 * <TileLayer url={tileUrl} attribution={attribution} />
 */
export const useLeafletMap = (options: UseLeafletMapOptions = {}): UseLeafletMapReturn => {
    const { mapset = 'basic' } = options;

    const getTileUrl = useMemo(() => {
        return (ms: MapsetType): string => {
            return `${MAPY_API_CONFIG.tiles.baseUrl}/${ms}/256/{z}/{x}/{y}?apikey=${getApiKey()}`;
        };
    }, []);

    const tileUrl = useMemo(() => getTileUrl(mapset), [mapset, getTileUrl]);

    const attribution = useMemo(() => {
        return '&copy; <a href="https://api.mapy.com/copyright" target="_blank">Seznam.cz a.s. a další</a>';
    }, []);

    return {
        tileUrl,
        attribution,
        mapsets: MAPY_API_CONFIG.tiles.mapsets,
        getTileUrl,
    };
};

export default useLeafletMap;
